import type { Permission } from "@shared/constants/permissions";

import { ROLES } from "@shared/constants/roles";
import { defineStore } from "pinia";
import { ref, computed } from "vue";

import authApi, { type AuthResponse, type UserData } from "@/api/auth";
import { impersonationApi } from "@/api/impersonation";
import {
  identifyUser,
  resetAnalytics,
  startSessionRecording,
  stopSessionRecording,
  trackEvent,
  setCurrentUserImpersonating,
  syncFeatureFlagFirm,
} from "@/services/analytics";
import { resetSentryUser, setSentryUser } from "@/services/monitoring";
import { piiLaneFor } from "@/utils/piiLane";

import { useAdminStore } from "./adminStore";
import { useCampaignStore } from "./campaignStore";
import { useChatStore } from "./chatStore";
import { useDataSourcesStore } from "./dataSourcesStore";
import { useFieldValueStore } from "./fieldValueStore";
import { useIngestionStore } from "./ingestionStore";
import { usePortalCampaignsStore } from "./portalCampaignsStore";
import { usePortalRangeStore } from "./portalRangeStore";
import { useStageCategoriesStore } from "./stageCategoriesStore";
import { useTableStateStore } from "./tableStateStore";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<null | UserData>(null);
  const accessToken = ref<null | string>(null);
  const refreshToken = ref<null | string>(null);
  const tokenExpiry = ref<null | number>(null);
  const loading = ref(false);
  const error = ref<null | string>(null);
  const impersonationData = ref<{
    originalUserEmail: string;
    originalUserId: string;
    startedAt: string;
  } | null>(null);

  /**
   * Caches the in-flight (or settled) initAuth() promise so the navigation
   * guard can await it via whenReady() instead of deciding route gates —
   * notably the terms-of-use gate — on a half-hydrated localStorage payload
   * (RAP-350). Null until initAuth() is first invoked.
   */
  const initAuthPromise = ref<null | Promise<void>>(null);

  // Token refresher timer
  let refreshTimer: null | number = null;

  // Track whether a refresh is needed when a background tab becomes visible
  let needsRefreshOnVisible = false;

  // Listen for tab visibility changes to handle background tabs gracefully.
  // Browsers throttle setTimeout in hidden tabs, so the refresh timer may not
  // fire on time. When the user returns we re-evaluate token state instead of
  // silently sitting on an expired token or, worse, logging them out.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;

      if (needsRefreshOnVisible) {
        // A previous refresh failed while the tab was hidden — retry now
        needsRefreshOnVisible = false;
        refreshAccessToken();
      } else if (
        accessToken.value &&
        tokenExpiry.value &&
        tokenExpiry.value <= Date.now() &&
        refreshToken.value
      ) {
        // Token expired while tab was hidden (timer throttled) — refresh now
        refreshAccessToken();
      } else if (accessToken.value && tokenExpiry.value) {
        // Token still valid — re-schedule timer in case it drifted
        scheduleTokenRefresh();
      }
    });
  }

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
  const isLoading = computed(() => loading.value);
  const getError = computed(() => error.value);
  const getUser = computed(() => user.value);
  const isImpersonating = computed(() => !!impersonationData.value);
  const getImpersonationData = computed(() => impersonationData.value);
  const hasAcceptedTerms = computed(() => !!user.value?.termsAcceptedAt);
  const needsToAcceptTerms = computed(
    () =>
      isAuthenticated.value &&
      !isImpersonating.value &&
      !isSuperadmin.value &&
      !hasAcceptedTerms.value
  );

  // Role-based access control. Superadmin is an identity gate (internal
  // platform staff); capability checks use permissions, not role names.
  const isSuperadmin = computed(() => {
    return user.value?.roles?.some((role) => role.name === ROLES.SUPERADMIN) || false;
  });

  /**
   * Holds the grant-audit role: a handling firm's own login for seeing what it shares and
   * switching it off, and nothing else (RAP-588).
   *
   * A role check, and only ever used to SUBTRACT: hide the AI assistant, deny every route without
   * `allowGrantAudit`, pick the landing page. Permissions grant and cannot subtract, so "this
   * principal sees less than an ordinary member of its org" has no expression as one — which is
   * why this is not a violation of "capability checks use permissions, not role names" above.
   *
   * It is NOT used to decide what the account may reach. That is `VIEW_DATA_SHARING`, seeded onto
   * the role universally, checked by `hasAnyPermission` on the route and by
   * `requireGrantAuditAuthority` server-side. An earlier version gated the Data Sharing nav entry
   * on this flag because the role was seeded with zero permissions — a role name deciding
   * capability, which is exactly what the permission model replaces.
   *
   * Do not add positive-access checks here. If something should be reachable, give it a permission.
   */
  const isGrantAuditor = computed(() => {
    return user.value?.roles?.some((role) => role.name === ROLES.GRANT_AUDITOR) || false;
  });

  const isReferringOrg = computed(() => {
    return user.value?.organization?.orgType?.toUpperCase?.() === "REFERRING" || false;
  });

  const isMarketingOrg = computed(() => {
    return user.value?.organization?.orgType?.toUpperCase?.() === "MARKETING" || false;
  });

  /**
   * External user — has no organization and accesses data exclusively
   * through UserCampaignViewAssignment rows. Used by chat gates: external
   * users always have chat access (no aiEnabled kill switch applies, since
   * the kill switch is per-org and they belong to none).
   */
  const isExternalUser = computed(() => !user.value?.organizationId);

  /**
   * True iff the user has at least one usable view assignment — a
   * UserCampaignViewAssignment whose `viewId` is non-null. The backend
   * (/auth/me) already nulls `viewId` for blocked assignments and soft-deleted
   * views, so a non-null `viewId` means "has a real, live shared view". Gates
   * the report-consumer experience (/reports).
   */
  const hasAssignedView = computed(
    () => user.value?.viewAssignments?.some((v) => v.viewId !== null) ?? false
  );

  /**
   * Phase B gate: org has data sync enabled (CRM imports, sync controls,
   * alias management, user/firm management). Replaces the legacy
   * HANDLING-org gate for sync-flavoured features.
   */
  const isDataSyncEnabled = computed(() => {
    return user.value?.organization?.dataSyncEnabled === true;
  });

  const isDualRepEnabled = computed(() => {
    return user.value?.organization?.dualRepEnabled === true;
  });

  /** The org may set per-source sync intervals on its data sources (RAP-613). */
  const isVariableSyncFrequencyEnabled = computed(() => {
    return user.value?.organization?.variableSyncFrequencyEnabled === true;
  });

  /**
   * Entitlement gate: this user's firm is served the REFERRING PORTAL instead of the
   * handling-firm app (RAP-630).
   *
   * Unlike the flags above this does not read `user.organization` — the backend resolves it at
   * the firm's identity anchor and returns it top-level, precisely so two colleagues hanging
   * off different canonicals cannot be served different applications.
   *
   * Fail-closed on `=== true`: the field is absent until /auth/me has answered, and "not known
   * yet" must mean "leave them on the app they already have", never "switch them over".
   *
   * This is UX only. It decides what renders; it does not decide what a portal API will serve.
   * That is `requireReferringPortal` on the backend, and it is not optional there.
   */
  const isReferringPortalEnabled = computed(() => {
    return user.value?.referringPortalEnabled === true;
  });

  /**
   * Phase B gate: org has AI features enabled (chat, smart fields, deep
   * insights, case health). Replaces the legacy HANDLING gate for AI features.
   */
  const isAiEnabled = computed(() => {
    return user.value?.organization?.aiEnabled === true;
  });

  /**
   * Phase B gate: function for per-campaign ownership checks. Returns true
   * when the campaign was created by ANY org in the current user's
   * accessible-org tree (root identity + canonicals + aliases). Mirrors
   * the backend's resolveCampaignAccess walk so the UI doesn't disagree
   * with the API: a user on a root identity whose canonical owns a
   * campaign correctly sees themselves as the owner.
   *
   * Used for: data access, view CRUD, view assignments, case assets, etc.
   *
   * Imperative helper — wrap in `computed()` if the `createdByOrgId` argument
   * is reactive so the result updates when the source changes.
   */
  function isCampaignOwner(createdByOrgId: null | string | undefined): boolean {
    if (!createdByOrgId) return false;
    const accessible = user.value?.accessibleOrgIds ?? [];
    return accessible.includes(createdByOrgId);
  }

  const isCaseHealthEnabled = computed(() => {
    return user.value?.organization?.caseHealthEnabled ?? false;
  });

  const isFalloutInsightsEnabled = computed(() => {
    // External (view-shared / null-org) viewers get the same reason-detail
    // UX as the owning firm — the per-org feature flag only gates the
    // firm-user UI, and the modal's data is already scoped server-side
    // per view so externals see only their accessible cases regardless.
    if (user.value && !user.value.organization) return true;
    return user.value?.organization?.aiFalloutInsightsEnabled ?? false;
  });

  // Permissions
  const permissions = computed<Permission[]>(() => {
    return user.value?.permissions ?? [];
  });

  /**
   * A referring-portal user who may not see claimant PII — the funder login (spec 2026-09-24).
   * Keyed on the MISSING permission rather than the role name, so a portal user whose role was
   * never configured gets the restricted portal, not the full one. Drives every funder hide:
   * Settings, Quick Ask, Capital writes, the search hints. The portal API masks names on its own
   * (`canViewPortalPii`), so this is presentation, not the boundary.
   */
  const isPortalFunder = computed(() => piiLaneFor(user.value) === "funder");

  /**
   * An org member without `view_pii` OUTSIDE a referring-portal firm (e.g. a handling-firm role
   * nobody granted PII). The server 403s all of their chat — only portal firm chat has a redacted
   * lane (RAP-693) — so the assistant is hidden for them rather than offered as a dead button.
   * Null-org external users are not affected: their PII is governed by their view.
   */
  const isChatHiddenForNoPii = computed(() => piiLaneFor(user.value) === "none");

  function hasPermission(permission: Permission): boolean {
    return permissions.value.includes(permission);
  }

  function hasAnyPermission(perms: Permission[]): boolean {
    if (perms.length === 0) return false;
    return perms.some((p) => permissions.value.includes(p));
  }

  function hasAllPermissions(perms: Permission[]): boolean {
    if (perms.length === 0) return false;
    return perms.every((p) => permissions.value.includes(p));
  }

  /**
   * Demo gate: org is a demo organization. Used to reveal demo-only UI
   * surfaces (Portfolio dashboards) that sales uses to show off unshipped
   * functionality. See docs/superpowers/specs/2026-04-08-demo-portfolio-pages-design.md
   */
  const isDemoOrg = computed(() => user.value?.organization?.isDemo === true);

  // Actions
  /**
   * Initialize auth from local storage.
   *
   * Caches the returned promise in `initAuthPromise` synchronously, before
   * the first await, so the navigation guard's whenReady() can await this
   * in-flight initialization rather than gating on a partial cached user.
   * See main.ts: app.use(router) schedules the initial navigation before
   * initAuth() is called, but both run synchronously to completion before
   * the guard's navigation microtask fires.
   */
  function initAuth(): Promise<void> {
    const run = async () => {
      const storedToken = localStorage.getItem("access_token");
      const storedRefreshToken = localStorage.getItem("refresh_token");
      const storedUser = localStorage.getItem("user");
      const storedExpiry = localStorage.getItem("token_expiry");

      if (storedToken) accessToken.value = storedToken;
      if (storedRefreshToken) refreshToken.value = storedRefreshToken;
      if (storedUser) user.value = JSON.parse(storedUser);
      if (storedExpiry) tokenExpiry.value = parseInt(storedExpiry, 10);

      // Schedule token refresh if we have a valid token
      if (accessToken.value && tokenExpiry.value && tokenExpiry.value > Date.now()) {
        scheduleTokenRefresh();
        // Check if we're currently impersonating (must be synchronous to disable analytics before any tracking)
        checkImpersonationStatus();

        // Always re-fetch user data to pick up current feature flags and org settings.
        // `/auth/login` stores only minimal user payload; localStorage cache may be stale.
        await fetchUserData();
      } else if (refreshToken.value) {
        // Try an immediate refresh if token is expired but we have refresh token
        await refreshAccessToken();
      } else {
        // Clear any bad data if tokens are invalid
        resetAuth();
      }
    };

    initAuthPromise.value = run();
    return initAuthPromise.value;
  }

  /**
   * Resolve once any in-flight auth initialization has settled. The
   * navigation guard awaits this so route gating — especially the
   * terms-of-use gate — decides on authoritative /auth/me data, not a
   * partial localStorage payload. Resolves immediately when no
   * initialization has run (e.g. in unit tests), so navigation is never
   * blocked. Init failures are swallowed: initAuth() does not reject in
   * practice (fetchUserData/refreshAccessToken catch internally), and a
   * rejected init must not surface as a thrown navigation guard.
   */
  async function whenReady(): Promise<void> {
    if (initAuthPromise.value) {
      await initAuthPromise.value.catch(() => {});
    }
  }

  /**
   * Request a verification code to be sent to the email
   */
  async function requestVerificationCode(email: string) {
    loading.value = true;
    error.value = null;
    const lowercasedEmail = email.toLowerCase();

    try {
      await authApi.requestCode({ email: lowercasedEmail });

      return true;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      error.value = err.response?.data?.error || "Failed to send verification code";
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Login with email and verification code
   */
  async function login(email: string, code: string) {
    loading.value = true;
    error.value = null;
    const lowercasedEmail = email.toLowerCase();

    try {
      const response = await authApi.login({ code, email: lowercasedEmail });

      // Clear all cached data from previous user before setting new auth data
      clearAllStores();

      setAuthData(response.data);

      // Fetch complete user data including roles
      await fetchUserData();

      return true;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // Set error message but do not reset auth state on verification failure
      error.value = err.response?.data?.error || "Invalid verification code";
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Returns true when the error is transient (network down, 5xx) and worth
   * retrying, as opposed to a definitive auth rejection (4xx) where the
   * backend is up and explicitly telling us the token is bad.
   */
  function isTransientError(err: unknown): boolean {
    // Axios errors carry { response?: { status: number } }.
    // If there's no response at all it's a network-level failure (transient).
    const errObj = err as { response?: { status?: number } };
    if (!errObj?.response) return true;
    // 5xx — server is struggling but might recover
    return (errObj.response.status ?? 0) >= 500;
  }

  // Deduplicates concurrent refresh calls. The timer, visibility handler, and
  // axios interceptor can all trigger refreshAccessToken at the same time —
  // without this, concurrent calls with the same refresh token would race.
  // If the backend ever adopts rotating refresh tokens the second call would
  // get a 401 and log the user out despite the first call succeeding.
  let activeRefresh: null | Promise<boolean> = null;

  /**
   * Refresh the access token.
   *
   * Two distinct failure modes:
   *
   * 1. **Auth rejection (401/403)** — the backend is up and says the refresh
   *    token is invalid or expired. Retrying won't help → logout immediately.
   *
   * 2. **Transient failure (network error, 5xx)** — the backend is down or
   *    restarting (e.g. during a deploy). Does a few quick retries (2 s apart),
   *    then falls back to a slower background retry every 15 s. The user is
   *    never logged out for transient errors — we just keep trying until the
   *    backend recovers.
   *
   * Concurrent calls are deduplicated — all callers share the same in-flight
   * refresh and receive the same result.
   */
  async function refreshAccessToken(maxRetries = 2): Promise<boolean> {
    if (!refreshToken.value) return false;

    // If a refresh is already in flight, piggyback on it
    if (activeRefresh) return activeRefresh;

    activeRefresh = executeRefresh(maxRetries).finally(() => {
      activeRefresh = null;
    });
    return activeRefresh;
  }

  async function executeRefresh(maxRetries: number): Promise<boolean> {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await authApi.refreshToken({
          accessToken: accessToken.value || undefined, // Send current token to preserve impersonation data
          refreshToken: refreshToken.value!,
        });
        const { accessToken: newToken, expiresIn } = response.data;

        // Update token and expiry
        accessToken.value = newToken;
        tokenExpiry.value = Date.now() + expiresIn * 1000;

        // Save to localStorage
        localStorage.setItem("access_token", newToken);
        localStorage.setItem("token_expiry", tokenExpiry.value.toString());

        // Check impersonation status after token refresh (token may have changed)
        checkImpersonationStatus();

        // Refresh user data to keep permissions and profile in sync with server.
        // Also needed if impersonation just ended (e.g. token expired overnight
        // and refresh couldn't preserve impersonation data — localStorage still
        // has stale user).
        await fetchUserData();

        // Re-sync Sentry + analytics identity — it can change across a refresh
        // (impersonation ending is the documented case above), and nothing else
        // on this path updates them; without this, events keep reporting under
        // the previous user/org until a reload or logout.
        if (user.value) {
          syncFeatureFlagFirm(user.value);
          identifyUser(user.value.id, user.value.email);
          setSentryUser({
            impersonating: isImpersonating.value,
            organizationId: user.value.organization?.id,
            orgType: user.value.organization?.orgType,
            role: user.value.roles?.[0]?.name,
            userId: user.value.id,
          });
        } else {
          resetSentryUser();
        }

        // Schedule next refresh
        needsRefreshOnVisible = false;
        scheduleTokenRefresh();

        return true;
      } catch (err) {
        // Auth rejection (401/403) — token is genuinely invalid, no point retrying
        if (!isTransientError(err)) {
          // Same monitoring/analytics resets as logout(): resetAuth() alone
          // leaves the prior identity attached to events fired between now and
          // the axios interceptor's hard redirect to /auth/login.
          stopSessionRecording();
          resetAnalytics();
          resetSentryUser();
          resetAuth();
          return false;
        }

        // Transient error — quick retry if we have attempts left
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          continue;
        }

        // Quick retries exhausted but this is transient — the backend is down,
        // not rejecting us. Keep retrying in the background every 15 s until it
        // recovers. The user stays "logged in" with a stale access token; if
        // they interact and hit a 401 the interceptor will await the next
        // successful refresh before retrying their request.
        needsRefreshOnVisible = true;
        scheduleTransientRetry();
        return false;
      }
    }

    return false; // Unreachable, satisfies TS
  }

  /**
   * Keep retrying token refresh every 15 s after transient failures.
   * Reuses the same timer slot as the normal refresh schedule — when the
   * refresh eventually succeeds it calls scheduleTokenRefresh() which
   * replaces this with the normal 14-minute cadence.
   */
  function scheduleTransientRetry() {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }
    refreshTimer = window.setTimeout(() => {
      refreshAccessToken();
    }, 15_000);
  }

  /**
   * Schedule token refresh 1 minute before expiry
   */
  function scheduleTokenRefresh() {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    if (!tokenExpiry.value) return;

    const currentTime = Date.now();
    const timeUntilExpiry = tokenExpiry.value - currentTime;

    // Refresh 1 minute before expiry, or immediately if less than 1 minute remains
    const refreshTime = Math.max(timeUntilExpiry - 60000, 0);

    refreshTimer = window.setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  }

  /**
   * Logout user
   */
  async function logout(allDevices = false) {
    if (!refreshToken.value) return;

    // Analytics: Track logout before resetting
    trackEvent("User Logged Out", {
      logoutType: allDevices ? "all_devices" : "single_device",
      organizationType: user.value?.organization?.orgType,
    });

    // Analytics: Stop session recording and reset
    stopSessionRecording();
    resetAnalytics();
    resetSentryUser();

    try {
      await authApi.logout({
        accessToken: accessToken.value || undefined,
        allDevices,
        refreshToken: refreshToken.value,
      });
    } finally {
      resetAuth();
      // Force page reload to clear all Pinia stores and cached data
      window.location.href = "/auth/login";
    }
  }

  /**
   * Logout current user without redirect
   * Used for magic link switch-account flow where we want to sign out
   * the current user and immediately sign in as a different user
   */
  async function logoutWithoutRedirect(allDevices = false) {
    // Clear monitoring/analytics context so events during (or after a failed)
    // account switch aren't attributed to the prior user. On success,
    // setAuthData() re-identifies as the new user.
    stopSessionRecording();
    resetAnalytics();
    resetSentryUser();

    if (!refreshToken.value) {
      resetAuth();
      return;
    }

    try {
      await authApi.logout({
        accessToken: accessToken.value || undefined,
        allDevices,
        refreshToken: refreshToken.value,
      });
    } finally {
      resetAuth();
    }
  }

  /**
   * Clear all Pinia stores to remove cached data from previous user
   */
  function clearAllStores() {
    // Each of these is a setup store with its own $reset (Pinia's built-in one is a no-op on setup
    // stores in production, which is how they went uncleared). One store failing to reset must not
    // stop the others or the sign-out, but it must not go unnoticed again either: the failure is
    // rethrown on its own tick, where the global handler (and Sentry) sees it.
    // Each store is built inside its own try too: a setup that throws mustn't stop the others.
    const stores = [
      useCampaignStore,
      useAdminStore,
      useFieldValueStore,
      useChatStore,
      usePortalCampaignsStore,
      usePortalRangeStore,
      useTableStateStore,
      useIngestionStore,
      useStageCategoriesStore,
      useDataSourcesStore,
    ];
    for (const useStore of stores) {
      try {
        useStore().$reset();
      } catch (err) {
        setTimeout(() => {
          const reason = err instanceof Error ? err.message : String(err);
          throw new Error(`Failed to clear the ${useStore.$id} store on sign-out: ${reason}`);
        });
      }
    }
  }

  /**
   * Reset authentication state
   */
  function resetAuth() {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    tokenExpiry.value = null;

    // Clear local storage
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("token_expiry");

    // Clear refresh timer
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    // Clear all other stores when resetting auth
    clearAllStores();
  }

  /**
   * Set authentication data after successful login
   */
  function setAuthData(authData: AuthResponse) {
    const { accessToken: token, expiresIn, refreshToken: refToken, user: userData } = authData;

    // Update state
    accessToken.value = token;
    refreshToken.value = refToken;
    tokenExpiry.value = Date.now() + expiresIn * 1000;
    user.value = userData;

    // Save to localStorage
    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", refToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token_expiry", tokenExpiry.value.toString());

    // Check impersonation status first (disables analytics if impersonating)
    checkImpersonationStatus();

    // Analytics: Identify user and start session recording (skipped if impersonating)
    syncFeatureFlagFirm(userData);
    identifyUser(userData.id, userData.email);
    setSentryUser({
      impersonating: isImpersonating.value,
      organizationId: userData.organization?.id,
      orgType: userData.organization?.orgType,
      role: userData.roles?.[0]?.name,
      userId: userData.id,
    });
    startSessionRecording();

    scheduleTokenRefresh();
  }

  /**
   * Get current user data from the API
   */
  async function fetchUserData() {
    if (!accessToken.value) return null;

    try {
      const response = await authApi.getUser();
      user.value = response.data;
      localStorage.setItem("user", JSON.stringify(user.value));
      // /auth/me is the first payload that names the root identity; re-point flag targeting at it.
      syncFeatureFlagFirm(response.data);
      return user.value;
    } catch (_err) {
      return null;
    }
  }

  /**
   * Check if currently impersonating by decoding the JWT token
   */
  function checkImpersonationStatus() {
    if (!accessToken.value) {
      impersonationData.value = null;
      setCurrentUserImpersonating(false);
      return;
    }

    try {
      // Decode JWT token to check for impersonation metadata
      const base64Url = accessToken.value.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      const decoded = JSON.parse(jsonPayload);

      if (decoded.impersonation) {
        impersonationData.value = {
          originalUserEmail: decoded.impersonation.originalUserEmail,
          originalUserId: decoded.impersonation.originalUserId,
          startedAt: decoded.impersonation.startedAt,
        };
        setCurrentUserImpersonating(true);
      } else {
        impersonationData.value = null;
        setCurrentUserImpersonating(false);
      }
    } catch (_err) {
      // If token decoding fails, clear impersonation data
      impersonationData.value = null;
      setCurrentUserImpersonating(false);
    }
  }

  /**
   * Stop impersonating and return to original user
   */
  async function stopImpersonation() {
    if (!impersonationData.value) return false;

    // Errors propagate to the caller (matches startImpersonation). The
    // ImpersonationBanner relies on this to reset its "Stopping…" flag —
    // swallowing here latches the Exit button until the user logs out.
    const response = await impersonationApi.stopImpersonation();

    // Update both localStorage and the in-memory ref so anything reading
    // `authStore.accessToken` directly (e.g. the 401-retry path in axios.ts)
    // doesn't see the stale impersonation token in the window between this
    // call and the hard navigation below.
    localStorage.setItem("access_token", response.token);
    accessToken.value = response.token;
    // Permissions are set to [] here to satisfy the UserData.permissions type
    // contract. This is safe because the redirect to "/" below triggers a full
    // page load, which runs initAuth → fetchUserData() and resolves real
    // permissions from the server before any guard or PermissionGate runs.
    localStorage.setItem(
      "user",
      JSON.stringify({
        email: response.user.email,
        id: response.user.id,
        name:
          `${response.user.firstName || ""} ${response.user.lastName || ""}`.trim() ||
          response.user.email,
        organization: response.user.organization ?? undefined,
        organizationId: response.user.organization?.id,
        permissions: [],
        roles: response.user.roles,
      })
    );

    // Clear in-memory impersonation state before the redirect so the banner
    // hides immediately if anything delays/blocks the navigation.
    impersonationData.value = null;
    setCurrentUserImpersonating(false);

    // Redirect to dashboard to avoid landing on pages the superadmin might not have access to
    window.location.href = "/";

    return true;
  }

  /**
   * Start impersonating a user (called from SuperadminUsers)
   */
  async function startImpersonation(userId: string) {
    const response = await impersonationApi.impersonate(userId);

    // Update both localStorage and the in-memory ref so anything reading
    // `authStore.accessToken` directly doesn't see the stale pre-impersonation
    // token in the window before the hard reload below.
    localStorage.setItem("access_token", response.token);
    accessToken.value = response.token;
    // Permissions are set to [] here to satisfy the UserData.permissions type
    // contract. This is safe because window.location.reload() below triggers
    // a full page load, which runs initAuth → fetchUserData() and resolves
    // real permissions from the server before any guard or PermissionGate runs.
    localStorage.setItem(
      "user",
      JSON.stringify({
        email: response.user.email,
        id: response.user.id,
        name:
          `${response.user.firstName || ""} ${response.user.lastName || ""}`.trim() ||
          response.user.email,
        organization: response.user.organization ?? undefined,
        organizationId: response.user.organization?.id,
        permissions: [],
        roles: response.user.roles,
      })
    );

    // Hard refresh to clear all Pinia stores and start fresh with new user context
    window.location.reload();

    return response;
  }

  /**
   * Set the terms accepted date for the current user
   *
   * Update state and localStorage.
   * TODO: Better to use Vue Query to fetch user so all we have to do is invalidate the query.
   */
  function setTermsAccepted(termsAcceptedAt: string) {
    if (user.value) {
      user.value = { ...user.value, termsAcceptedAt };
      localStorage.setItem("user", JSON.stringify(user.value));
    }
  }

  return {
    accessToken,
    error,
    loading,
    // State
    user,

    getError,
    getImpersonationData,
    getUser,
    hasAcceptedTerms,
    hasAssignedView,
    // Getters
    isAiEnabled,
    isAuthenticated,
    isCampaignOwner,
    isCaseHealthEnabled,
    isChatHiddenForNoPii,
    isDataSyncEnabled,
    isDemoOrg,
    isDualRepEnabled,
    isExternalUser,
    isFalloutInsightsEnabled,
    isGrantAuditor,
    isImpersonating,
    isLoading,
    isMarketingOrg,
    isPortalFunder,
    isReferringOrg,
    isReferringPortalEnabled,
    isSuperadmin,
    isVariableSyncFrequencyEnabled,
    needsToAcceptTerms,
    permissions,

    checkImpersonationStatus,
    fetchUserData,
    hasAllPermissions,
    hasAnyPermission,
    hasPermission,
    // Actions
    initAuth,
    login,
    logout,
    logoutWithoutRedirect,
    refreshAccessToken,
    requestVerificationCode,
    resetAuth,
    setAuthData,
    setTermsAccepted,
    startImpersonation,
    stopImpersonation,
    whenReady,

    // Helpers
    get token() {
      return accessToken.value;
    },

    setToken(newToken: string) {
      accessToken.value = newToken;
      localStorage.setItem("access_token", newToken);
      // Check impersonation status when token changes
      checkImpersonationStatus();
    },

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setUser(userData: any) {
      user.value = userData;
      localStorage.setItem("user", JSON.stringify(userData));
    },
  };
});
